import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from '../../theme/ThemeContext';
import { usePermissions } from '../../context/PermissionContext';
import useLabOrderStore from '../../stores/labOrderStore';
import LabOrderCard, { statusMeta } from './LabOrderCard';
import LabReportModal from './LabReportModal';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import { isRecentlyCompleted } from '../../utils/completedWindow';
import { isImagingType, labQueueToken, labEntryAt } from '../../constants/lab';
import { printSampleLabel } from '../../utils/printSampleLabel';
import { groupLaneByPatientDay, labEntryDay } from './labQueueGrouping';
import { getTodayIST } from '../../utils/dateIST';
import labCardIdentity from './labCardIdentity';
import useLabQueueMoveFeedback, { TOAST_LONG_MS } from './useLabQueueMoveFeedback';

const EMPTY_REPORT = { open: false, loading: false, error: null, blobUrl: null, contentType: null, fileName: null };

// Lane titles stay type-neutral — a lane can hold pathology samples AND imaging
// scans; the per-test wording lives on each card (Collect sample / Perform scan).
const LANES = [
  { key: 'payment_cleared', title: 'Pending' },
  { key: 'sample_collected', title: 'Collected / scanned' },
  { key: 'in_progress', title: 'In progress' },
  // Completed is collapsed by default — it's reference, not the work-in-hand.
  { key: 'completed', title: 'Completed', collapsible: true },
];

// S7 (bk-219) — the four `window.prompt(...)` reason boxes replaced by ONE
// `reasonDialog` state + `ConfirmationModal`. `title`/`placeholder` are the
// EXACT prompt sentences the old prompts used, split verbatim (question vs.
// parenthetical) — no new microcopy. `icon`/`ctaLabel` mirror each action's
// own existing button (no emoji is duplicated as new copy, just re-shown).
const REASON_DIALOG_META = {
  reject: {
    icon: '↩', variant: 'danger',
    title: 'Why is this sample being rejected?',
    placeholder: 'e.g. hemolysed, insufficient, wrong tube — kept for the record',
    ctaLabel: 'Reject sample',
  },
  cancel: {
    icon: '✕', variant: 'danger',
    title: 'Why is this order being cancelled?',
    placeholder: 'kept for the record',
    ctaLabel: 'Cancel order', busyLabel: 'Cancelling…',
  },
  replace: {
    icon: '↻', variant: 'default',
    title: 'Reason for replacing this report?',
    placeholder: 'kept for the record',
    ctaLabel: 'Replace', busyLabel: 'Replacing…',
  },
  delete: {
    icon: '🗑', variant: 'danger',
    title: 'Reason for deleting this report?',
    placeholder: 'kept for the record',
    ctaLabel: 'Delete', busyLabel: 'Deleting…',
  },
};

// A lane a technician is expected to work through in full; beyond this, the
// search box (which already filters before grouping) is the long-list
// answer, and the tail is named rather than silently truncated.
const MAX_VISIBLE_PATIENT_BLOCKS = 20;

// Merge groupLaneByPatientDay's per-day groups for the SAME patient into one
// render block, LANE-WIDE (not just consecutive) — T7's day-first lane sort
// makes the lane day-major, so a patient's yesterday-group and today-group
// can sit apart with another patient's group of that same day between them
// (D1). A block is created at the position of the patient's FIRST group in
// the incoming (already day-ascending-sorted) lane, so it still lands at
// their oldest open day; every later group for that patient folds into the
// same block instead of opening a second header. Purely presentational —
// the selector already owns the grouping; this only decides how groups draw.
function toPatientBlocks(dayGroups) {
  const blocks = [];
  const byPatient = new Map();
  for (const g of dayGroups) {
    const patientKey = g.orders[0]?.patient_id || g.patientName;
    let block = byPatient.get(patientKey);
    if (!block) {
      block = {
        key: g.key,
        patientKey,
        patientName: g.patientName,
        ipNumber: g.orders[0]?.ip_number,
        dayGroups: [],
      };
      byPatient.set(patientKey, block);
      blocks.push(block);
    }
    block.dayGroups.push(g);
  }
  // Day-groups inside a block always read oldest-first (mockup 02), even for
  // a lane (e.g. Completed) whose own sort is recency-based rather than
  // day-ascending.
  for (const block of blocks) {
    block.dayGroups.sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
  }
  return blocks;
}

// D5 (owner ruling) — the calendar date is ALWAYS shown, never bare
// "Today"/"Yesterday": "Today · 8 Sep", "Yesterday · 7 Sep", "6 Sep" for
// anything older. `day` is an IST 'YYYY-MM-DD' string (never a raw ISO
// timestamp), so this never touches `toISOString()`.
function shortDateIST(day) {
  const d = new Date(`${day}T00:00:00+05:30`);
  if (isNaN(d)) return day;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
}
// IST 'YYYY-MM-DD' for "yesterday" — single source for both `dayLabel` below
// and S6's Pending "oldest" hint, so the two never compute it two ways.
function getYesterdayIST() {
  return new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}
function dayLabel(day) {
  if (!day) return '';
  const short = shortDateIST(day);
  if (day === getTodayIST()) return `Today · ${short}`;
  if (day === getYesterdayIST()) return `Yesterday · ${short}`;
  return short;
}

// S6 — the word naming the OLDEST carried-over day in Pending's hint
// ("… — oldest Yesterday" / "… — oldest 5 Sep"). Never "Today" here — this is
// only ever called on a day that already failed the `!== today` older test.
function oldestDayWord(day) {
  if (!day) return '';
  if (day === getYesterdayIST()) return 'Yesterday';
  return shortDateIST(day);
}

// Lowercase haystack for the search box — patient, test, type, queue number,
// and (AC8) mobile, UHID and receipt number, all read through the ONE
// labCardIdentity projection so the searchable set never drifts from what
// the card actually shows.
function matchesQuery(o, q) {
  if (!q) return true;
  const token = labQueueToken(o) ?? '';
  const identity = labCardIdentity(o);
  const hay = `${o.patient_name || ''} ${o.test_name || ''} ${o.specimen_type || ''} l-${token} q-${token} #${token} ${identity.mobile || ''} ${identity.uhid || ''} ${identity.receipt || ''}`.toLowerCase();
  return hay.includes(q);
}

// The name used in S5's status-change stripe/toast/aria text — the same
// name the identity block already shows, falling back the same way the
// block header does when it's unresolved.
function patientLabelFor(order) {
  return labCardIdentity(order).name || order?.patient_id || 'Patient';
}

// R2-3 — "paid" for the Cancel-order refund line: `is_paid`/`payment_id` are
// the fields lab orders already carry for this (same ones `LabReportActions`
// and `groupAdvisedByTest` read); a minted receipt (the SAME field the
// card's own receipt chip reads, via `labCardIdentity`) implies it too.
function isOrderPaid(order) {
  return !!(order?.is_paid || order?.payment_id || labCardIdentity(order).receipt);
}

// S6 — the one-line hint under each status card's count. `count`/`older` are
// ALWAYS read from `byLane` (the same array the lane header badge counts) —
// this never keeps its own tally, so a card can't drift from its lane.
function laneHint(key, count, older, oldestWord) {
  if (key === 'payment_cleared') {
    if (count === 0) return 'nothing waiting';
    if (older > 0) return `${count} · ${older} older — oldest ${oldestWord}`;
    return `${count} today`;
  }
  // R2-1 — only Pending is day-split; a bare "today" here would misreport
  // yesterday's still-uncollected-and-unstarted samples as today's.
  if (key === 'sample_collected') return count === 0 ? 'nothing waiting' : `${count} awaiting processing`;
  if (key === 'in_progress') return count === 0 ? 'nothing waiting' : `${count} processing`;
  if (key === 'completed') return 'last 3 days';
  return '';
}

/**
 * LabQueueView — the technician's lab queue lanes + actions (no page chrome).
 * Used by LabQueuePage (a route) and by the lab-tech dashboard (SmartWorkflowWidget).
 */
export default function LabQueueView() {
  const { isDark } = useTheme();
  const { hasPermission, memberId } = usePermissions();
  const canEdit = hasPermission('EXECUTE_LAB_ORDER');
  const canCancel = hasPermission('CANCEL_LAB_ORDER');
  const {
    myQueue, isLoading, error, busyId, fetchMyQueue, setMyMemberId,
    collectSpecimen, start, uploadReportsAndComplete, attachReports, fetchLatestReportBlob,
    replaceLatestReport, deleteLatestReport, rejectSample, cancel,
  } = useLabOrderStore();

  // Inline report viewer (avoids popup-blocked window.open).
  const [report, setReport] = useState(EMPTY_REPORT);
  // Search across the queue (patient / test / queue #).
  const [query, setQuery] = useState('');
  // Collect-sample dialog: confirms patient + lets the tech set/auto-assign
  // the sample number. { order, value } — confirming closes the dialog and
  // starts the hold (S5); the actual API call (and any failure it returns)
  // happens later, off the dialog, so it carries no busy/error state of its
  // own any more.
  const [collectState, setCollectState] = useState(null);
  // Complete confirmation after the result file(s) are picked. { order, files }.
  const [completeState, setCompleteState] = useState(null);
  // Completed lane is collapsed by default (reference, not active work).
  const [showCompleted, setShowCompleted] = useState(false);
  // S6 — status-card filter. `null` = "All" (every lane shown, the default
  // and byte-identical layout to before S6). Owner ruling: NOT remembered —
  // plain component state, no localStorage/store/URL, so it's always "All"
  // on a fresh mount/remount.
  const [activeLane, setActiveLane] = useState(null);
  // A toast "Jump →" for a card whose lane isn't the current filter (S6):
  // switch the filter first, then scroll once that lane's card is on screen
  // (registerCardRef only exists for a RENDERED card).
  const [pendingScrollId, setPendingScrollId] = useState(null);
  // R2-2 — Completed is collapsed by default (`showCompleted`); filtering to
  // JUST that lane must not land on a collapsed row with no cards. Remembers
  // whatever `showCompleted` was before the filter forced it open, so
  // returning to All (or any other lane) restores it exactly.
  const showCompletedBeforeFilterRef = useRef(null);
  // S5 — "stay, then move" status-change feedback (holds, glow, toast,
  // aria-live). Purely local UI state; every `run()` below is one of the
  // EXISTING lifecycle actions above, just called after the hold instead of
  // immediately.
  const {
    holds, glow, toast, announcement,
    startHold, undoHold, flush, notify, registerCardRef, scrollToCard,
  } = useLabQueueMoveFeedback();

  // One ~1s clock drives every card's live "waiting"/elapsed figure
  // (seconds-accurate) without per-card timers.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchMyQueue();
  }, [fetchMyQueue]);

  // Tell the store who's viewing, so a realtime LAB_ORDER_ASSIGNED upsert can
  // target this technician's own orders (instant, no manual refresh).
  useEffect(() => {
    if (memberId) setMyMemberId(memberId);
  }, [memberId, setMyMemberId]);

  // S6 — fires AFTER the render that switched `activeLane` (if the jump
  // needed to), by which point the destination lane's card is registered.
  useEffect(() => {
    if (!pendingScrollId) return;
    scrollToCard(pendingScrollId);
    setPendingScrollId(null);
  }, [pendingScrollId, activeLane, scrollToCard]);

  const byLane = useMemo(() => {
    const q = query.trim().toLowerCase();
    const m = { payment_cleared: [], sample_collected: [], in_progress: [], completed: [] };
    for (const o of myQueue) {
      if (!matchesQuery(o, q)) continue;
      // A standing-order parent is not a technician work item — never lane it
      // (it also has no assignee, so it shouldn't reach a tech's queue at all).
      if (o.status === 'standing') continue;
      // 3-day cap on the Completed lane; pending/in-progress always show.
      if (o.status === 'completed' && !isRecentlyCompleted(o)) continue;
      if (m[o.status]) m[o.status].push(o);
    }
    // Stable order in every lane so cards don't reshuffle on refetch / realtime:
    // Completed = most-recently-done first; others by queue number then time,
    // with a lab_order_id tiebreaker.
    const ts = (o) => Date.parse(o?.completed_at || labEntryAt(o) || '') || 0;
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => {
        if (k === 'completed') {
          const d = ts(b) - ts(a);
          if (d) return d;
        } else {
          // `lab_queue_number` resets to 1 every day per org (T7) — sort the
          // older IST day first so a still-open yesterday's #L-1 lists above
          // today's #L-1 instead of interleaving by token alone.
          const da = labEntryDay(a);
          const db = labEntryDay(b);
          if (da !== db) return da < db ? -1 : 1;
          const qa = labQueueToken(a) ?? Number.MAX_SAFE_INTEGER;
          const qb = labQueueToken(b) ?? Number.MAX_SAFE_INTEGER;
          if (qa !== qb) return qa - qb;
          const d = ts(a) - ts(b);
          if (d) return d;
        }
        return String(a.lab_order_id || '').localeCompare(String(b.lab_order_id || ''));
      });
    }
    return m;
  }, [myQueue, query]);

  // Collect goes through a confirm dialog (right patient + sample number);
  // Start ("Send for processing") stays one tap — the card already shows
  // patient + test above it. Both now HOLD (S5, mockup variant A): the
  // actual API call is deferred until the 3s hold expires (or flushed
  // early) so an accidental tap has a real Undo — nothing is sent until
  // then. Collect's stripe still reuses `statusMeta`'s OWN "what just
  // happened" label (ratified as built). Start/complete were re-worded by
  // business (Required change 3 — "Processing — moving to In progress" and
  // "Completed — moving to Completed" read like the screen was broken); those
  // two are fixed verbs, not derived from `statusMeta`.
  const onAction = (key, order) => {
    if (key === 'collect') { setCollectState({ order, value: '' }); return; }
    if (key === 'start') {
      const imaging = isImagingType(order.specimen_type);
      const laneTitle = LANES.find((l) => l.key === 'in_progress')?.title;
      const startVerb = imaging ? 'Sent for reporting' : 'Sent for processing';
      startHold(order.lab_order_id, {
        tone: 'ok',
        content: <>✓ {startVerb} — moving to <b>{laneTitle}</b></>,
        laneTitle,
        laneKey: 'in_progress',
        patientLabel: patientLabelFor(order),
        glowTag: 'Just landed',
        allowUndo: true,
        run: () => start(order.lab_order_id),
      });
    }
  };
  const closeCollect = () => setCollectState(null);
  // Confirming the sample-number dialog no longer calls the API inline — it
  // closes the dialog and starts the hold; `collectSpecimen` fires only when
  // the hold expires (or is flushed). A failure at that point (e.g. a
  // duplicate sample number) surfaces via the toast instead of inline in the
  // (already-closed) dialog — see useLabQueueMoveFeedback's `fire()`.
  const confirmCollect = () => {
    if (!collectState) return;
    const { order, value } = collectState;
    const typed = value.trim();
    setCollectState(null);
    const imaging = isImagingType(order.specimen_type);
    const laneTitle = LANES.find((l) => l.key === 'sample_collected')?.title;
    startHold(order.lab_order_id, {
      tone: 'ok',
      content: <>✓ {statusMeta('sample_collected', imaging).label} — moving to <b>{laneTitle}</b></>,
      laneTitle,
      laneKey: 'sample_collected',
      patientLabel: patientLabelFor(order),
      glowTag: 'Just landed',
      allowUndo: true,
      run: () => collectSpecimen(order.lab_order_id, typed ? { sample_number: typed } : {}),
    });
  };

  const onComplete = (order, files) => setCompleteState({ order, files });
  // The confirmation dialog still gates "Complete with result" — the hold
  // applies AFTER that confirm, exactly like collect's sample-number dialog.
  const confirmComplete = () => {
    if (!completeState) return;
    const { order, files } = completeState;
    setCompleteState(null);
    const laneTitle = LANES.find((l) => l.key === 'completed')?.title;
    startHold(order.lab_order_id, {
      tone: 'ok',
      content: <>✓ Result published — moving to <b>{laneTitle}</b></>,
      laneTitle,
      laneKey: 'completed',
      patientLabel: patientLabelFor(order),
      glowTag: 'Just landed',
      allowUndo: true,
      run: () => uploadReportsAndComplete(order.lab_order_id, files),
    });
  };

  // S7 (bk-219) — Reject / Cancel / Replace / Delete all required a typed
  // reason via `window.prompt(...)`, the phone's own plain grey popup — the
  // one place in the queue that didn't match the rest of the app. Replaced
  // with the SAME app dialog + reason textarea the IPD "Cancel lab order"
  // screen already ships (`DiagnosticsCard.jsx`) — one `reasonDialog` state
  // drives all four kinds. Copy is the existing prompt sentences, split
  // verbatim into a title (the question) + placeholder (its parenthetical) —
  // no new microcopy.
  const [reasonDialog, setReasonDialog] = useState(null); // { kind, order, file?, reason }
  const [reasonBusy, setReasonBusy] = useState(false);
  const closeReasonDialog = () => { if (!reasonBusy) setReasonDialog(null); };

  // Reject bounces the order straight back to Pending; a brief (holdMs: 0)
  // stripe + landing glow narrate that instead of the card silently
  // reappearing there. Opening the dialog first FLUSHES any forward hold
  // already pending on this same card — a competing action must never leave
  // a tap unsent (unchanged from before S7; only the reason CAPTURE moved
  // off `window.prompt`).
  const onReject = (order) => {
    flush(order.lab_order_id);
    setReasonDialog({ kind: 'reject', order, reason: '' });
  };
  // Cancel a not-started order (sample not yet collected). Once collected the
  // card shows Reject sample instead — the same boundary the backend enforces.
  const onCancel = (order) => {
    flush(order.lab_order_id);
    setReasonDialog({ kind: 'cancel', order, reason: '' });
  };
  const onReplace = (order, file) => {
    setReasonDialog({ kind: 'replace', order, file, reason: '' });
  };
  const onDelete = (order) => {
    setReasonDialog({ kind: 'delete', order, reason: '' });
  };

  const confirmReasonDialog = async () => {
    if (!reasonDialog) return;
    const reason = reasonDialog.reason.trim();
    if (!reason) return;
    const { kind, order, file } = reasonDialog;
    if (kind === 'reject') {
      setReasonDialog(null);
      const laneTitle = LANES.find((l) => l.key === 'payment_cleared')?.title;
      startHold(order.lab_order_id, {
        tone: 'warn',
        content: (
          <>↩ Sample rejected — moving back to <b>{laneTitle}</b> for re-collection
            <span className="opacity-80 font-semibold"> · {reason}</span>
          </>
        ),
        laneTitle,
        laneKey: 'payment_cleared',
        patientLabel: patientLabelFor(order),
        glowTag: 'Needs re-collection',
        allowUndo: false,
        holdMs: 0,
        run: () => rejectSample(order.lab_order_id, { reason }),
      });
      return;
    }
    setReasonBusy(true);
    if (kind === 'cancel') {
      const patientLabel = patientLabelFor(order);
      const r = await cancel(order.lab_order_id, { reason });
      if (r?.success) {
        notify(`${patientLabel} · ${order.test_name} — cancelled (${reason})`, {
          announce: `${patientLabel} order cancelled`,
          durationMs: TOAST_LONG_MS,
        });
      }
    } else if (kind === 'replace') {
      await replaceLatestReport(order.lab_order_id, file, reason);
    } else if (kind === 'delete') {
      await deleteLatestReport(order.lab_order_id, reason);
    }
    setReasonBusy(false);
    setReasonDialog(null);
  };

  const onPrintLabel = (order) => printSampleLabel({
    sample_number: order.sample_number,
    patient_name: order.patient_name || order.patient_id,
    test_name: order.test_name,
    specimen_type: order.specimen_type,
    queue_number: labQueueToken(order),
  });

  const onAttach = (order, files) => attachReports(order.lab_order_id, files);
  // Shared by View AND Print (S9) — the SAME fetch, the SAME inline modal
  // open; Print additionally fires the browser print dialog once the blob
  // is ready. No new endpoint, no second fetch path.
  const loadReportBlob = async (order) => {
    setReport({ ...EMPTY_REPORT, open: true, loading: true });
    const r = await fetchLatestReportBlob(order.lab_order_id);
    if (r.success) {
      setReport({
        open: true, loading: false, error: null,
        blobUrl: r.data.blobUrl, contentType: r.data.contentType, fileName: r.data.fileName,
      });
    } else {
      setReport({ ...EMPTY_REPORT, open: true, loading: false, error: r.error || 'Failed to load report.' });
    }
    return r;
  };
  const onViewReport = (order) => loadReportBlob(order);
  // S9 — "Print report" on a completed card with an attached report. Prints
  // via a hidden iframe pointed at the SAME blob URL the viewer already
  // renders (mirrors printSampleLabel/printToken's hidden-iframe technique) —
  // printing is a convenience on top of viewing, so a print failure never
  // blocks the modal that's already open.
  const onPrintReport = async (order) => {
    const r = await loadReportBlob(order);
    if (!r?.success || !r.data?.blobUrl) return;
    try {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) { /* noop */ }
        setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 1000);
      };
      document.body.appendChild(iframe);
      iframe.src = r.data.blobUrl;
    } catch (_) { /* noop */ }
  };
  const closeReport = () => {
    setReport((r) => {
      if (r.blobUrl) { try { URL.revokeObjectURL(r.blobUrl); } catch (_) { /* noop */ } }
      return EMPTY_REPORT;
    });
  };

  // S6 — status cards replacing the plain lane titles as the queue header.
  // EVERY number here reads straight off `byLane` (the exact same array the
  // lane header badge below counts) — no second tally, so a card can never
  // drift from its lane, and both follow the search box together (byLane is
  // already query-filtered).
  const todayIST = getTodayIST();
  const pendingOrders = byLane.payment_cleared;
  const olderPendingCount = pendingOrders.filter((o) => labEntryDay(o) !== todayIST).length;
  const oldestPendingDay = olderPendingCount > 0
    ? pendingOrders.reduce((min, o) => {
        const d = labEntryDay(o);
        if (d === todayIST) return min;
        return (!min || d < min) ? d : min;
      }, null)
    : null;
  const oldestWord = oldestDayWord(oldestPendingDay);
  const allCount = LANES.reduce((n, l) => n + byLane[l.key].length, 0);
  const statCards = [
    { key: null, title: 'All', count: allCount, hint: 'across every stage' },
    ...LANES.map((l) => ({
      key: l.key,
      title: l.title,
      count: byLane[l.key].length,
      hint: laneHint(l.key, byLane[l.key].length, olderPendingCount, oldestWord),
    })),
  ];
  // Owner ruling: NOT remembered — `activeLane` is plain component state
  // (defaults null = "All" on every mount), so a filter never survives a
  // remount/nav-away. `null` renders every lane, byte-identical to before S6.
  const visibleLanes = activeLane ? LANES.filter((l) => l.key === activeLane) : LANES;

  // R2-2 — the ONE place `activeLane` changes (stat-card tap, "clear", the
  // S5 toast jump). Entering the Completed filter force-expands it in the
  // SAME event/render as the lane switch (so the very first render of the
  // filtered view already has cards, not a collapsed row with nothing for
  // `scrollToCard` to find); leaving it restores whatever `showCompleted`
  // was before.
  const selectLane = (next) => {
    if (next === 'completed' && activeLane !== 'completed') {
      showCompletedBeforeFilterRef.current = showCompleted;
      setShowCompleted(true);
    } else if (activeLane === 'completed' && next !== 'completed' && showCompletedBeforeFilterRef.current !== null) {
      setShowCompleted(showCompletedBeforeFilterRef.current);
      showCompletedBeforeFilterRef.current = null;
    }
    setActiveLane(next);
  };

  return (
    <div>
      {/* S5 accessibility — the same move/reject/cancel wording read out for
          a screen-reader user, independent of the stripe/toast visuals. */}
      <div aria-live="polite" className="sr-only">{announcement}</div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
            🔬 My Lab Queue
          </h1>
          <p className="text-sm" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
            Collect the specimen, run the test, complete it. Turnaround (TAT) is timed from collection.
          </p>
        </div>
        <button
          onClick={() => fetchMyQueue()}
          disabled={isLoading}
          className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3 py-1.5 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
        >
          {isLoading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* S6 — five big tappable status cards replace the plain lane titles as
          the queue header. Tap one to see only that stage (full width, the
          other lanes aren't even in the DOM); tap it again or "All" to
          reset. NOT remembered (owner ruling) — `activeLane` is plain state,
          always "All" on a fresh mount. */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" role="group" aria-label="Filter by stage">
        {statCards.map((c) => {
          const active = activeLane === c.key;
          return (
            <button
              key={c.key ?? 'all'}
              type="button"
              data-testid={`stat-card-${c.key ?? 'all'}`}
              aria-pressed={active}
              onClick={() => selectLane(active ? null : c.key)}
              className={`relative text-left rounded-xl border-2 px-3 py-2.5 ${
                active
                  ? 'border-teal-600 dark:border-teal-400 bg-teal-50 dark:bg-teal-900/30'
                  : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
              }`}
            >
              {active && (
                <span className="absolute top-1.5 right-2 text-teal-700 dark:text-teal-300 font-black text-xs">✓</span>
              )}
              <div className={`text-[11px] font-extrabold uppercase tracking-wide ${active ? 'text-teal-700 dark:text-teal-300' : 'text-slate-500 dark:text-slate-400'}`}>
                {c.key ? '● ' : ''}{c.title}
              </div>
              <div className="text-xl font-extrabold mt-0.5" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
                {c.count}
              </div>
              <div className="text-[11px] font-semibold mt-0.5 text-slate-400 dark:text-slate-500">
                {c.hint}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search patient, mobile, UHID, receipt, test, or queue #…"
          className="flex-1 min-w-[200px] rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
        />
        {activeLane && (
          <button
            type="button"
            onClick={() => selectLane(null)}
            className="rounded-full border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-xs font-extrabold px-3 py-1.5 whitespace-nowrap"
          >
            ✕ Showing {LANES.find((l) => l.key === activeLane)?.title} only — clear
          </button>
        )}
      </div>

      {visibleLanes.map((lane) => {
        const count = byLane[lane.key].length;
        const expanded = !lane.collapsible || showCompleted;
        const badge = (
          <span data-testid={`lane-badge-${lane.key}`} className="rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 text-[11px]">
            {count}
          </span>
        );
        return (
        <section key={lane.key} className="mt-5">
          {lane.collapsible ? (
            <button
              type="button"
              onClick={() => setShowCompleted((v) => !v)}
              aria-expanded={expanded}
              className="flex w-full items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <span className="text-[10px] text-slate-400 dark:text-slate-500">{expanded ? '▾' : '▸'}</span>
              {lane.title}
              {badge}
              {!expanded && count > 0 && (
                <span className="normal-case font-semibold text-slate-400 dark:text-slate-500">— tap to view</span>
              )}
            </button>
          ) : (
            <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
              {lane.title}
              {badge}
            </div>
          )}
          {expanded && (count === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 text-sm text-center py-4">
              —
            </div>
          ) : (
            <div className="space-y-3">
              {(() => {
                const blocks = toPatientBlocks(groupLaneByPatientDay(byLane[lane.key]));
                // Day labels are a LANE-wide decision (T7), not a per-patient
                // one: a lane holding yesterday's patient A #L-1 and today's
                // patient B #L-1 needs a day label on both, even though each
                // patient's own block is single-day.
                //
                // The trigger is "the lane holds ANY day that is not today",
                // NOT "the lane holds more than one day" (business round 1).
                // A lane made up ENTIRELY of yesterday's carried-over orders
                // is a single day, so the old `size > 1` test hid the labels
                // on exactly the case the technician most needs to see — the
                // stale work — and it read as if it were all today's.
                // A lane holding ONLY today's orders still renders exactly as
                // before, with no labels at all — the T6 guarantee.
                const laneDays = new Set(blocks.flatMap((b) => b.dayGroups.map((g) => g.day)).filter(Boolean));
                const today = getTodayIST();
                const showDays = [...laneDays].some((d) => d !== today);
                const visibleBlocks = blocks.slice(0, MAX_VISIBLE_PATIENT_BLOCKS);
                const hiddenCount = blocks.length - visibleBlocks.length;
                return (
                  <>
                    {visibleBlocks.map((block) => {
                      const totalTests = block.dayGroups.reduce((n, g) => n + g.orders.length, 0);
                      // D2 — a block can hold TWO different lab tokens (a
                      // patient's second same-day lab visit). The header
                      // shows one token only when every order in the block
                      // shares it; otherwise it's omitted from the header and
                      // each card shows its own `#L-n` (`showToken` below) —
                      // no token is ever invisible.
                      const blockTokens = block.dayGroups.flatMap((g) => g.orders.map((o) => labQueueToken(o)));
                      const headerToken = blockTokens.length > 0 && blockTokens.every((t) => t === blockTokens[0])
                        ? blockTokens[0]
                        : null;
                      return (
                        <div
                          key={block.key}
                          className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2"
                        >
                          <div className="flex flex-wrap items-center gap-1.5 px-1 pb-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
                            <span>{block.patientName}</span>
                            {block.ipNumber && (
                              <span className="rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700 px-1.5 text-[10px] font-bold">
                                {block.ipNumber}
                              </span>
                            )}
                            {headerToken != null && (
                              <span className="text-slate-500 dark:text-slate-400 font-semibold">· #L-{headerToken}</span>
                            )}
                            {totalTests > 1 && (
                              <span className="ml-auto rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 text-[10px]">
                                {totalTests} tests
                              </span>
                            )}
                          </div>
                          {block.dayGroups.map((g) => {
                            const label = showDays ? dayLabel(g.day) : null;
                            // bk-1089 — the chip style keys off the DAY, not
                            // off the rendered label. D5 changed `dayLabel` to
                            // return "Today · 8 Sep", so the old
                            // `label === 'Today'` test never matched again and
                            // today's chip silently took the amber
                            // stale/overdue style — inverting the very cue T7
                            // exists for. Today = calm slate, any older day =
                            // amber.
                            const isToday = g.day === today;
                            return (
                              <div key={g.key}>
                                {label && (
                                  <div
                                    className={
                                      isToday
                                        ? 'inline-block mb-1.5 ml-1 rounded px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                        : 'inline-block mb-1.5 ml-1 rounded px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                                    }
                                  >
                                    {label}
                                  </div>
                                )}
                                <div className="space-y-2">
                                  {g.orders.map((o) => {
                                    const hold = holds[o.lab_order_id];
                                    const isGlowing = glow?.id === o.lab_order_id;
                                    return (
                                      <div key={o.lab_order_id} ref={(el) => registerCardRef(o.lab_order_id, el)}>
                                        <LabOrderCard
                                          order={o}
                                          hidePatient
                                          showToken={headerToken == null}
                                          nowMs={nowMs}
                                          busy={busyId === o.lab_order_id}
                                          onAction={onAction}
                                          onComplete={onComplete}
                                          onAttach={canEdit ? onAttach : undefined}
                                          onViewReport={onViewReport}
                                          onPrintReport={onPrintReport}
                                          onReplace={canEdit ? onReplace : undefined}
                                          onDelete={canEdit ? onDelete : undefined}
                                          onReject={canEdit ? onReject : undefined}
                                          onCancel={canCancel ? onCancel : undefined}
                                          onPrintLabel={onPrintLabel}
                                          stripe={hold ? {
                                            tone: hold.tone,
                                            content: hold.content,
                                            onUndo: hold.allowUndo ? () => undoHold(o.lab_order_id) : null,
                                          } : null}
                                          glow={isGlowing}
                                          glowTag={isGlowing ? glow.tag : null}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                    {hiddenCount > 0 && (
                      <div className="px-1 text-xs font-semibold text-teal-700 dark:text-teal-300">
                        + {hiddenCount} more — search to narrow
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ))}
        </section>
        );
      })}

      <LabReportModal {...report} onClose={closeReport} />

      {/* Collect-sample dialog — confirm the patient and set/auto-assign the
          unique sample number before the specimen is recorded. */}
      {collectState && (() => {
        const o = collectState.order;
        const imaging = isImagingType(o.specimen_type);
        // The old `o.patient_mobile` read was dead (the field didn't exist on
        // the order until S4) — resolve through the shared projection so the
        // line shows a real mobile when known and is omitted, not blank,
        // otherwise (AC5).
        const identity = labCardIdentity(o);
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={closeCollect}
          >
            <div
              className="w-full max-w-sm rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-extrabold text-slate-800 dark:text-slate-100">
                {imaging ? '📷 Perform scan' : '🧫 Collect sample'}
              </h3>
              <div className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">
                <div className="font-semibold text-slate-800 dark:text-slate-100">{o.test_name}</div>
                <div className="mt-0.5">
                  {o.patient_name || o.patient_id}
                  {o.queue_number != null ? ` · #Q-${o.queue_number}` : ''}
                  {identity.mobile ? ` · ${identity.mobile}` : ''}
                </div>
              </div>

              <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Sample number
              </label>
              <input
                autoFocus
                type="text"
                value={collectState.value}
                onChange={(e) => setCollectState((s) => ({ ...s, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmCollect(); }}
                placeholder="Leave blank to auto-assign"
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                A unique number is auto-assigned if left blank. Type your own to match a pre-printed tube.
              </p>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={closeCollect}
                  className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3 py-1.5 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCollect}
                  className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
                >
                  {imaging ? 'Perform scan' : 'Collect sample'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Complete confirmation — shown after the result file(s) are picked. */}
      <ConfirmationModal
        isOpen={!!completeState}
        icon="✅"
        title="Complete this test?"
        message={completeState
          ? `${completeState.order.test_name} for ${completeState.order.patient_name || completeState.order.patient_id} — ${completeState.files.length} file(s) attached. The result will be available to the doctor.`
          : ''}
        ctaLabel="Complete & publish"
        cancelLabel="Cancel"
        onConfirm={confirmComplete}
        onCancel={() => setCompleteState(null)}
      />

      {/* S7 (bk-219) — the one Reject/Cancel/Replace/Delete reason dialog,
          replacing all four window.prompt(...) calls. Reuses the exact
          ConfirmationModal + textarea pattern DiagnosticsCard's IPD "Cancel
          lab order" dialog already ships. */}
      {reasonDialog && (() => {
        const meta = REASON_DIALOG_META[reasonDialog.kind];
        const order = reasonDialog.order;
        return (
          <ConfirmationModal
            isOpen
            icon={meta.icon}
            variant={meta.variant}
            title={meta.title}
            message={(
              <>
                {patientLabelFor(order)} · {order.test_name}
                <label
                  className="block text-left text-[11.5px] font-semibold text-slate-500 dark:text-slate-400 mt-3 mb-1"
                  htmlFor="lab-reason-dialog-input"
                >
                  Reason (required)
                </label>
                <textarea
                  id="lab-reason-dialog-input"
                  autoFocus
                  className="w-full min-h-[56px] px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
                  placeholder={meta.placeholder}
                  maxLength={300}
                  value={reasonDialog.reason}
                  onChange={(e) => setReasonDialog((d) => (d ? { ...d, reason: e.target.value } : d))}
                />
                {/* R2-3 — a paid order refunds through the front desk, not
                    this dialog; named up front so the technician isn't
                    surprised later. Unpaid/credit orders show nothing extra. */}
                {reasonDialog.kind === 'cancel' && isOrderPaid(order) && (
                  <div className="mt-2 text-[11.5px] font-semibold text-amber-700 dark:text-amber-300">
                    This test is already paid — the front desk will refund it.
                  </div>
                )}
              </>
            )}
            ctaLabel={reasonBusy ? (meta.busyLabel || meta.ctaLabel) : meta.ctaLabel}
            cancelLabel="Keep it"
            ctaDisabled={!reasonDialog.reason.trim() || reasonBusy}
            onConfirm={confirmReasonDialog}
            onCancel={closeReasonDialog}
          />
        );
      })()}

      {/* S5 bottom toast — names the move/cancel; tapping it (when it has a
          target card) scrolls there. Purely presentational; the aria-live
          region above already carries the same information for a
          screen-reader user.
          S6 — if the card moved into a lane hidden by the current status-card
          filter, jump first SELECTS that lane (`toast.laneKey`, via the same
          `selectLane` the stat cards use — so a jump INTO Completed also
          expands it, R2-2), THEN scrolls once the card is actually rendered
          (the `pendingScrollId` effect above fires after that same render,
          not before). "All" (`null`) already shows every lane — only
          actually SWITCH away from it when the destination isn't already the
          active filter, so jump on the default "All" view keeps behaving
          exactly as before S6. Completed is the one exception: even on "All"
          it can still be individually COLLAPSED, so a jump there expands it
          regardless of whether the filter itself changes. */}
      {toast && (
        <div className="fixed left-1/2 bottom-5 -translate-x-1/2 z-50 max-w-[92vw] rounded-xl bg-slate-900 dark:bg-slate-100 text-slate-100 dark:text-slate-900 px-4 py-2.5 text-sm font-bold shadow-2xl flex items-center gap-3">
          <span>{toast.text}</span>
          {toast.targetId && (
            <button
              onClick={() => {
                if (toast.laneKey && activeLane !== null && activeLane !== toast.laneKey) {
                  selectLane(toast.laneKey);
                } else if (toast.laneKey === 'completed' && !showCompleted) {
                  setShowCompleted(true);
                }
                setPendingScrollId(toast.targetId);
              }}
              className="text-teal-300 dark:text-teal-700 underline font-extrabold whitespace-nowrap"
            >
              Jump →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
